import { useCallback } from "react";
import { Node, Edge } from "reactflow";
import { message } from "antd";
import { 
  VariablePlaceholder,
  ToolCall,
  ActionType,
  NodeData
} from "../utils/types";
import { ModalState, DeleteModalState } from "../hooks/hooks";
import {
  debugLog,
  extractPlaceholders,
  getNodeChildren,
  getNextNodeId,
  resetNodeIdCounter,
  deduplicateEdges,
  resolveIdentifierToNodeId,
} from "../utils/utils";
import { operatorNeedsValue } from "../utils/validation";

interface UseNodeOperationsProps {
  nodes: Node[];
  edges: Edge[];
  setNodes: (nodes: Node[] | ((prev: Node[]) => Node[])) => void;
  setEdges: (edges: Edge[] | ((prev: Edge[]) => Edge[])) => void;
  modalState: ModalState;
  deleteModalState: DeleteModalState;
  placeholders: Record<string, VariablePlaceholder>;
  setPlaceholders: (placeholders: Record<string, VariablePlaceholder>) => void;
  saveToHistory: (actionType: ActionType) => void;
  identifiers: string[];
  setIdentifiers: (identifiers: string[] | ((prev: string[]) => string[])) => void;
  globalVariableRegistry: Record<string, VariablePlaceholder>;
  setGlobalVariableRegistry: (registry: Record<string, VariablePlaceholder> | ((prev: Record<string, VariablePlaceholder>) => Record<string, VariablePlaceholder>)) => void;
  isFirstNode: boolean;
  setIsFirstNode: (isFirstNode: boolean) => void;

  hasChildWithIdentifier: (parentId: string, identifier: string) => boolean;
  setModalState: (state: ModalState | ((prev: ModalState) => ModalState)) => void;
  resetModalState: () => void;
  resetDeleteModalState: () => void;
  getNodeDescendants: (nodeId: string, allNodes: Node[], allEdges: Edge[]) => string[];
  initialNodes: Node[];
  initialEdges: Edge[];
  updateNodeHandlers: (currentEdges?: Edge[]) => void;
  setManuallyMovedNodes: React.Dispatch<React.SetStateAction<Set<string>>>;
  repositionTemporaryDecisionChildren: (decisionNodeId: string, retryCount?: number) => void;
  nodeDimensions: Record<string, number>;
}

export const useNodeOperations = ({
  nodes,
  edges,
  setNodes,
  setEdges,
  modalState,
  deleteModalState,
  placeholders,
  setPlaceholders,
  saveToHistory,
  identifiers,
  setIdentifiers,
  globalVariableRegistry,
  setGlobalVariableRegistry,
  isFirstNode,
  setIsFirstNode,

  hasChildWithIdentifier,
  setModalState,
  resetModalState,
  resetDeleteModalState,
  getNodeDescendants,
  initialNodes,
  initialEdges,
  updateNodeHandlers,
  setManuallyMovedNodes,
  repositionTemporaryDecisionChildren,
  nodeDimensions,
}: UseNodeOperationsProps) => {

  // Style used for upstream (back-link/loop) connections
  const UPSTREAM_EDGE_STYLE = { stroke: "#1677ff", strokeDasharray: "2 6", strokeWidth: 2 } as const;

  // Determine if connecting source -> target would be upstream based on coordinates.
  // Primary rule: target meaningfully above source (lower y = higher on canvas) counts as upstream.
  // Secondary (tie) rule: if roughly same y, a strong left-backflow (target far left of source) also counts.
  // Fallback: if positions missing, use ancestry check.
  const isUpstreamConnection = useCallback((sourceId: string, targetId: string, allNodes: Node[], allEdges: Edge[]) => {
    try {
      const sourceNode = allNodes.find(n => n.id === sourceId);
      const targetNode = allNodes.find(n => n.id === targetId);
      if (sourceNode?.position && targetNode?.position) {
        const VERTICAL_THRESHOLD = 12; // px tolerance
        const HORIZONTAL_BACKFLOW_THRESHOLD = -120; // px to the left of source
        const dy = targetNode.position.y - sourceNode.position.y;
        const dx = targetNode.position.x - sourceNode.position.x;
        if (dy < -VERTICAL_THRESHOLD) return true; // clearly above
        if (Math.abs(dy) <= VERTICAL_THRESHOLD && dx < HORIZONTAL_BACKFLOW_THRESHOLD) return true; // same row but flowing back left
        return false;
      }
      // Fallback to topology if positions aren't available
      const descendantsOfTarget = getNodeDescendants(targetId, allNodes, allEdges);
      return descendantsOfTarget.includes(sourceId);
    } catch (_e) {
      return false;
    }
  }, [getNodeDescendants]);

  // Helper function to get node dimensions with fallbacks and collision avoidance
  const getNodeDimensions = useCallback((nodeId: string) => {
    const DEFAULT_NODE_WIDTH = 280;
    const DEFAULT_NODE_HEIGHT = 120;
    const MIN_HORIZONTAL_SPACING = 50;
    const MIN_VERTICAL_SPACING = 180;
    
    const height = nodeDimensions[nodeId] || DEFAULT_NODE_HEIGHT;
    // For width, we use the default since we don't track width changes
    const width = DEFAULT_NODE_WIDTH;
    
    // Calculate base spacing
    const baseHorizontalSpacing = Math.max(MIN_HORIZONTAL_SPACING, width * 0.2);
    const baseVerticalSpacing = Math.max(MIN_VERTICAL_SPACING, height + 60);
    
    // Get the current node to check its children count
    const currentNode = nodes.find(n => n.id === nodeId);
    const childCount = edges.filter(edge => edge.source === nodeId).length;
    
    // Increase spacing for nodes with many children to reduce edge crossings
    const childrenSpacingMultiplier = Math.min(1 + (childCount * 0.15), 2.0); // Cap at 2x spacing
    
    // Increase spacing based on tree density in the area
    const nearbyNodesCount = nodes.filter(node => {
      if (!node.position || node.id === nodeId) return false;
      const distance = Math.sqrt(
        Math.pow((currentNode?.position?.x || 0) - node.position.x, 2) + 
        Math.pow((currentNode?.position?.y || 0) - node.position.y, 2)
      );
      return distance < 400; // Count nodes within 400px radius
    }).length;
    
    const densitySpacingMultiplier = Math.min(1 + (nearbyNodesCount * 0.05), 1.5); // Cap at 1.5x spacing
    
    return {
      width,
      height,
      // Apply multipliers to reduce collisions
      horizontalSpacing: baseHorizontalSpacing * childrenSpacingMultiplier * densitySpacingMultiplier,
      verticalSpacing: baseVerticalSpacing * Math.max(childrenSpacingMultiplier, densitySpacingMultiplier)
    };
  }, [nodeDimensions, nodes, edges]);

  // Helper function to check if a node has children (is not a "free child")
  const nodeHasChildren = useCallback((nodeId: string) => {
    // Check regular edges
    const hasRegularChildren = edges.some(edge => edge.source === nodeId);
    
    // Check decision paths
    const node = nodes.find(n => n.id === nodeId);
    const hasDecisionChildren = node?.data.entityType === "DECISION" && 
      node.data.decisionData && 
      (node.data.decisionData.truePath || node.data.decisionData.falsePath);
    
    return hasRegularChildren || hasDecisionChildren;
  }, [nodes, edges]);

  // Smart positioning logic - reusable for both node creation and reorganization
  const calculateSmartPosition = useCallback((parentNode: Node, childNode: Node, allNodes: Node[], allEdges: Edge[]) => {
    const NODE_WIDTH = 280;
    const HORIZONTAL_SPACING = 150;
         const VERTICAL_SPACING = 180;
    
    // Special positioning logic for decision nodes
    if (parentNode.data.entityType === "DECISION") {
      const decisionData = parentNode.data.decisionData || { truePath: null, falsePath: null, conditions: [] };
      const childIdentifier = childNode.data.identifier || childNode.id;
      
      // Find both True and False path nodes to check their current positions
      const truePathNode = allNodes.find(n => 
        (n.data.identifier === decisionData.truePath || n.id === decisionData.truePath) && 
        decisionData.truePath !== null
      );
      const falsePathNode = allNodes.find(n => 
        (n.data.identifier === decisionData.falsePath || n.id === decisionData.falsePath) && 
        decisionData.falsePath !== null
      );
      
      // Check if both nodes exist and if they're overlapping or have clear left/right positions
      if (truePathNode && falsePathNode) {
        const OVERLAP_THRESHOLD = NODE_WIDTH * 0.8; // Consider overlapping if within 80% of node width
        const xDiff = Math.abs(truePathNode.position.x - falsePathNode.position.x);
        
        
        // If they're not overlapping, preserve their relative positioning
        if (xDiff > OVERLAP_THRESHOLD) {
          const trueIsLeft = truePathNode.position.x < falsePathNode.position.x;
          
          if (decisionData.truePath === childIdentifier) {
            // Position True path based on its current relative position
            const xOffset = trueIsLeft ? -HORIZONTAL_SPACING : HORIZONTAL_SPACING;
            return {
              x: parentNode.position.x + xOffset,
              y: parentNode.position.y + VERTICAL_SPACING
            };
          } else if (decisionData.falsePath === childIdentifier) {
            // Position False path based on its current relative position  
            const xOffset = trueIsLeft ? HORIZONTAL_SPACING : -HORIZONTAL_SPACING;
            return {
              x: parentNode.position.x + xOffset,
              y: parentNode.position.y + VERTICAL_SPACING
            };
          }
        }
      }
      
      // Default/fallback positioning when nodes are overlapping or don't exist yet
      // True→Left, False→Right
      let xOffset = 0; // Default center
      if (decisionData.truePath === childIdentifier) {
        // True path goes to the left
        xOffset = -HORIZONTAL_SPACING;
      } else if (decisionData.falsePath === childIdentifier) {
        // False path goes to the right  
        xOffset = HORIZONTAL_SPACING;
      }
      
      return {
        x: parentNode.position.x + xOffset,
        y: parentNode.position.y + VERTICAL_SPACING
      };
    } else {
      // Regular node positioning logic with centering (for any non-DECISION node)
      const childNodeIds = getNodeChildren(parentNode.id, allEdges);
      const existingChildren = childNodeIds.map(id => allNodes.find(n => n.id === id)).filter(Boolean);
      
      // Calculate total number of children (including the one we're about to add)
      const totalChildren = existingChildren.length + 1;
      
      if (existingChildren.length === 0) {
        // First child - position directly below parent (will be centered)
        return {
          x: parentNode.position.x,
          y: parentNode.position.y + VERTICAL_SPACING
        };
      } else {
        // Multiple children - calculate centered positions
        const totalWidth = (totalChildren - 1) * (NODE_WIDTH + (HORIZONTAL_SPACING / 2));
        const startX = parentNode.position.x - (totalWidth / 2);
        const childIndex = existingChildren.length; // This will be the index of the new child
        
        return {
          x: startX + childIndex * (NODE_WIDTH + (HORIZONTAL_SPACING / 2)),
          y: parentNode.position.y + VERTICAL_SPACING
        };
      }
    }
  }, [getNodeChildren]);

  // Function to reposition all children of a parent to be centered
  const repositionChildrenCentered = useCallback((parentNodeId: string, allNodes: Node[], allEdges: Edge[], alreadyPositionedNodes?: Set<string>) => {
    // Get dynamic dimensions for the parent node
    const parentDimensions = getNodeDimensions(parentNodeId);
    
    const parentNode = allNodes.find(n => n.id === parentNodeId);
    if (!parentNode) {
      return allNodes;
    }
    if (parentNode.data.entityType === "DECISION") {
      return allNodes; // Skip decision nodes - they use special left/right positioning
    }
    
    const childNodeIds = getNodeChildren(parentNodeId, allEdges);
    const children = childNodeIds.map(id => allNodes.find(n => n.id === id)).filter(Boolean) as Node[];
    
    
    if (children.length === 0) {
      return allNodes;
    }
    
    if (children.length === 1) {
      const child = children[0];
      
      // Check if the child has already been positioned during reorganization
      if (alreadyPositionedNodes && alreadyPositionedNodes.has(child.id)) {
        return allNodes; // Child has already been positioned, don't move it
      }
      
      // Single child with no other dependencies - position directly under parent
      
      // Mark this child as positioned
      if (alreadyPositionedNodes) {
        alreadyPositionedNodes.add(child.id);
      }
      
      return allNodes.map(node => {
        if (node.id === child.id) {
          const newX = parentNode.position.x;
          const newY = parentNode.position.y + parentDimensions.verticalSpacing;
          return {
            ...node,
            position: { x: newX, y: newY }
          };
        }
        return node;
      });
    }
    
    // Calculate centered positions for multiple children using dynamic spacing
    
    // Calculate total width needed for all children with dynamic spacing
    const childSpacing = parentDimensions.width + parentDimensions.horizontalSpacing;
    const totalWidth = (children.length - 1) * childSpacing;
    const startX = parentNode.position.x - (totalWidth / 2);
    
    
    // Update positions of all children
    return allNodes.map(node => {
      const childIndex = children.findIndex(child => child.id === node.id);
      if (childIndex !== -1) {
        // Check if the child has already been positioned during reorganization
        if (alreadyPositionedNodes && alreadyPositionedNodes.has(node.id)) {
          return node; // Child has already been positioned, don't move it
        }
        
        // Mark this child as positioned
        if (alreadyPositionedNodes) {
          alreadyPositionedNodes.add(node.id);
        }
        
        const newX = startX + childIndex * childSpacing;
        const newY = parentNode.position.y + parentDimensions.verticalSpacing;
        return {
          ...node,
          position: {
            x: newX,
            y: newY
          }
        };
      }
      return node;
    });
  }, [getNodeChildren, getNodeDimensions]);

  // Fast, height-aware retry positioner to avoid visible overlap
  const positionDecisionChildrenWithRetry = useCallback((decisionNodeId: string, decisionData: any, attempt: number = 0) => {
    if (!decisionData) return;
    setNodes(currentNodes => {
      const decisionNode = currentNodes.find(n => n.id === decisionNodeId);
      if (!decisionNode) return currentNodes;

      const HORIZONTAL_SPACING = 150;
      const measuredHeight = nodeDimensions[decisionNodeId];
      const VERTICAL_SPACING = (measuredHeight ? measuredHeight + 60 : 240);

      return currentNodes.map(node => {
        const nodeIdentifier = node.data.identifier || node.id;
        const isTruePath = decisionData.truePath === nodeIdentifier;
        const isFalsePath = decisionData.falsePath === nodeIdentifier;
        if (!isTruePath && !isFalsePath) return node;

        let xOffset = 0;
        if (isTruePath) xOffset = HORIZONTAL_SPACING;
        else if (isFalsePath) xOffset = -HORIZONTAL_SPACING;

        const finalPosition = {
          x: decisionNode.position.x + xOffset,
          y: decisionNode.position.y + VERTICAL_SPACING
        };

        return {
          ...node,
          position: finalPosition
        };
      });
    });

    // If we didn't have a measured height yet, retry quickly until we do
    if (!nodeDimensions[decisionNodeId] && attempt < 6) {
      setTimeout(() => positionDecisionChildrenWithRetry(decisionNodeId, decisionData, attempt + 1), 60);
    }
  }, [setNodes, nodeDimensions]);

  // Helper function to process response variables from tool calls and add them to global registry
  const processResponseVariables = useCallback((toolCalls: ToolCall[]) => {
    const newPlaceholders: Record<string, any> = {};
    
    toolCalls.forEach(toolCall => {
      if (toolCall.responseVariables) {
        toolCall.responseVariables.forEach(responseVar => {
          if (responseVar.name && responseVar.values && responseVar.values.length > 0) {
            // Check if this response variable already exists in the global registry
            if (!globalVariableRegistry[responseVar.name]) {
              newPlaceholders[responseVar.name] = {
                name: responseVar.name,
                values: responseVar.values,
                persist: false // Default persist to false
              };
            } else {
              // If it exists, merge the values (avoid duplicates)
              const existingValues = globalVariableRegistry[responseVar.name].values || [];
              const newValues = responseVar.values.filter(value => !existingValues.includes(value));
              if (newValues.length > 0) {
                newPlaceholders[responseVar.name] = {
                  ...globalVariableRegistry[responseVar.name],
                  values: [...existingValues, ...newValues]
                };
              }
            }
          }
        });
      }
    });
    
    // Update global registry if we have new placeholders
    if (Object.keys(newPlaceholders).length > 0) {
      setGlobalVariableRegistry(prev => ({
        ...prev,
        ...newPlaceholders
      }));
    }
  }, [globalVariableRegistry, setGlobalVariableRegistry]);

  const handleOk = useCallback(() => {
    
    // Check if we have valid content OR valid tool calls OR valid decision data
    const hasValidToolCalls = modalState.newNodeEntityType === "BOT" && modalState.hasToolCall && modalState.toolCalls.some((tc: any) => tc.name && tc.name.trim());
    const hasValidContent = modalState.newNodeContent && modalState.newNodeContent.trim();
    const hasValidDecisionData = modalState.newNodeEntityType === "DECISION" && 
      modalState.decisionData.conditions.length > 0 &&
      modalState.decisionData.conditions.every((cond: any) => 
        cond.variable && cond.operator && (!operatorNeedsValue(cond.operator) || cond.value)
      ) &&
      (modalState.decisionData.truePath || modalState.decisionData.falsePath);
    
    if ((!modalState.sourceNodeId && !modalState.isEditMode) || (!hasValidContent && !hasValidToolCalls && !hasValidDecisionData)) {
      return;
    }

    // If not editing and this is a new node, check for duplicate identifiers
    if (!modalState.isEditMode && modalState.sourceNodeId && modalState.newNodeIdentifier) {
      if (hasChildWithIdentifier(modalState.sourceNodeId, modalState.newNodeIdentifier)) {
        message.error(`This parent already has a child with the "${modalState.newNodeIdentifier}" identifier.`);
        return;
      }
    }

    // If editing, check if we're trying to change to an identifier that would create a duplicate
    if (modalState.isEditMode && modalState.nodeToEdit && modalState.newNodeIdentifier) {
      // Get parent ID
      const parentEdge = edges.find(edge => edge.target === modalState.nodeToEdit);
      if (parentEdge) {
        const parentId = parentEdge.source;
        // Check for siblings with same identifier, excluding this node
        const childrenIds = getNodeChildren(parentId, edges);
        const hasDuplicateIdentifier = childrenIds.some(childId => {
          const childNode = nodes.find(node => node.id === childId);
          return childNode && 
                 childNode.id !== modalState.nodeToEdit && // Exclude self
                 childNode.data.identifier === modalState.newNodeIdentifier;
        });
        
        if (hasDuplicateIdentifier) {
          message.error(`This parent already has another child with the "${modalState.newNodeIdentifier}" identifier.`);
          return;
        }
      }
    }

    // Check if we're trying to connect to an existing node (for decision nodes)
    if (!modalState.isEditMode && modalState.sourceNodeId && modalState.newNodeIdentifier) {
      const sourceNode = nodes.find(n => n.id === modalState.sourceNodeId);
      const isConnectingToDecisionNode = sourceNode?.data.entityType === "DECISION";
      
      if (isConnectingToDecisionNode) {
        // Check if a node with this identifier already exists in the tree
        const existingNode = nodes.find(n => n.data.identifier === modalState.newNodeIdentifier);
        
        if (existingNode) {
          // Node exists - check if it's a free child or has children
          const existingNodeHasChildren = nodeHasChildren(existingNode.id);
          
          // If the existing node has no children, we can reposition it properly
          if (!existingNodeHasChildren) {
            // Calculate proper position for this existing node using smart positioning
            const decisionData = sourceNode.data.decisionData || { truePath: null, falsePath: null, conditions: [] };
            const hasTruePath = !!decisionData.truePath;
            const hasFalsePath = !!decisionData.falsePath;
            
            // Determine if this will be true path or false path
            const willBeTruePath = !hasTruePath && !hasFalsePath ? true : 
                                  !hasTruePath && hasFalsePath ? true : 
                                  hasTruePath && !hasFalsePath ? false : false;
            
            const NODE_WIDTH = 280;
            const HORIZONTAL_SPACING = 150;
            const VERTICAL_SPACING = 180;
            
            // Smart positioning: check existing positions first
            let xOffset = 0;
            
            // Find both True and False path nodes to check their current positions
            const truePathNode = nodes.find(n => 
              (n.data.identifier === decisionData.truePath || n.id === decisionData.truePath) && 
              decisionData.truePath !== null
            );
            const falsePathNode = nodes.find(n => 
              (n.data.identifier === decisionData.falsePath || n.id === decisionData.falsePath) && 
              decisionData.falsePath !== null
            );
            
            // Check if both nodes exist and if they're overlapping or have clear left/right positions
            if (truePathNode && falsePathNode) {
              const OVERLAP_THRESHOLD = NODE_WIDTH * 0.8; // Consider overlapping if within 80% of node width
              const xDiff = Math.abs(truePathNode.position.x - falsePathNode.position.x);
              
              
              // If they're not overlapping, preserve their relative positioning
              if (xDiff > OVERLAP_THRESHOLD) {
                const trueIsLeft = truePathNode.position.x < falsePathNode.position.x;
                
                if (willBeTruePath) {
                  // Position True path based on its current relative position
                  xOffset = trueIsLeft ? -HORIZONTAL_SPACING : HORIZONTAL_SPACING;
                } else {
                  // Position False path based on its current relative position  
                  xOffset = trueIsLeft ? HORIZONTAL_SPACING : -HORIZONTAL_SPACING;
                }
              } else {
                // They're overlapping, use default positioning: True→Left, False→Right
                xOffset = willBeTruePath ? -HORIZONTAL_SPACING : HORIZONTAL_SPACING;
              }
            } else {
              // One or both nodes don't exist yet, use default positioning: True→Left, False→Right
              xOffset = willBeTruePath ? -HORIZONTAL_SPACING : HORIZONTAL_SPACING;
            }
            
            const newPosition = {
              x: sourceNode.position.x + xOffset,
              y: sourceNode.position.y + VERTICAL_SPACING
            };
            
            
            // Update the existing node's position
            setNodes(nds => nds.map(node => 
              node.id === existingNode.id 
                ? { ...node, position: newPosition }
                : node
            ));
            
            // Don't mark as manually positioned - allow reorganization to handle all nodes
          }
          
          // Link it to the decision node
          const decisionData = sourceNode.data.decisionData || { truePath: null, falsePath: null, conditions: [] };
          const hasTruePath = !!decisionData.truePath;
          const hasFalsePath = !!decisionData.falsePath;
          
          const updatedDecisionData = { ...decisionData };
          
          if (!hasTruePath && !hasFalsePath) {
            // No paths exist, set as true path
            updatedDecisionData.truePath = modalState.newNodeIdentifier;
          } else if (hasTruePath && !hasFalsePath) {
            // Only true path exists, set as false path
            updatedDecisionData.falsePath = modalState.newNodeIdentifier;
          } else if (!hasTruePath && hasFalsePath) {
            // Only false path exists, set as true path
            updatedDecisionData.truePath = modalState.newNodeIdentifier;
          } else {
            // Both paths exist - show error
            message.error("Decision node already has both true and false paths assigned.");
            return;
          }
          
          // Update the decision node with the new path
          setNodes(nds => nds.map(node => 
            node.id === modalState.sourceNodeId 
              ? { ...node, data: { ...node.data, decisionData: updatedDecisionData } }
              : node
          ));
          
          // Create decision edge with the appropriate label
          const isTrue = updatedDecisionData.truePath === modalState.newNodeIdentifier;
          const edgeLabel = isTrue ? "True" : "False";
          const labelStyle = isTrue 
            ? { fill: "#52c41a", fontWeight: 600 }
            : { fill: "#f5222d", fontWeight: 600 };
          const labelBgStyle = isTrue 
            ? { fill: "#f6ffed" }
            : { fill: "#fff2f0" };
          
          const decisionEdgeId = `e${modalState.sourceNodeId}-${existingNode.id}_${isTrue ? "true" : "false"}`;
          
          setEdges(eds => {
            // Check if edge already exists
            const existingEdge = eds.find(edge => edge.id === decisionEdgeId);
            if (existingEdge) {
              return eds; // Don't add duplicate
            }
            // Record connection as its own history action
            saveToHistory("connect");
            const isUp = isUpstreamConnection(modalState.sourceNodeId, existingNode.id, nodes, eds);
            const edgeStyle = isUp
              ? { ...UPSTREAM_EDGE_STYLE }
              : (isTrue ? { stroke: "#52c41a", strokeWidth: 2 } : { stroke: "#f5222d", strokeWidth: 2 });
            
            const decisionEdge: Edge = {
              id: decisionEdgeId,
              source: modalState.sourceNodeId,
              target: existingNode.id,
              type: "custom",
              targetHandle: "target",
              label: edgeLabel,
              style: edgeStyle,
              labelStyle,
              labelBgStyle,
              data: existingNode.data?.isDestinationNode ? { toDestination: true } : undefined
            };
            
            return [...eds, decisionEdge];
          });
          
          // Reset form state and exit early
          resetModalState();
          const repositionMessage = existingNodeHasChildren 
            ? `Successfully linked existing node "${modalState.newNodeIdentifier}" to decision path.`
            : `Successfully linked and repositioned existing node "${modalState.newNodeIdentifier}" to decision path.`;
          message.success(repositionMessage);
          return;
        }
      }
    }

    // Extract placeholders from content
    const detectedPlaceholders = extractPlaceholders(modalState.newNodeContent);
    const nodePlaceholders: Record<string, VariablePlaceholder> = {};
    
    // Initialize placeholders with current examples or from global registry
    detectedPlaceholders.forEach(name => {
      // Check if this placeholder exists in the global registry first
      if (globalVariableRegistry[name]) {
        // Use the global registry version (empty local placeholder to indicate it uses global)
        nodePlaceholders[name] = { name, values: [], persist: false };
      } else if (placeholders[name]) {
        // Use the local placeholder
        nodePlaceholders[name] = { 
          name, 
          values: placeholders[name]?.values || [],
          persist: placeholders[name]?.persist || false
        };
        
        // Add to global registry if not already there
        setGlobalVariableRegistry(prev => ({
          ...prev,
          [name]: placeholders[name]
        }));
      } else {
        // Create a new placeholder
        nodePlaceholders[name] = { name, values: [], persist: false };
        
        // Add to global registry
        setGlobalVariableRegistry(prev => ({
          ...prev,
          [name]: { name, values: [], persist: false }
        }));
      }
    });

    // Save current state to history if adding a new node
    if (!modalState.isEditMode) {
      saveToHistory("add");
    }

    // Flag to track if we need to reorganize the tree
    // Don't reorganize for child nodes since we position them intelligently
    const isAddingChildNode = !modalState.isEditMode && modalState.sourceNodeId;
    const sourceNode = nodes.find(n => n.id === modalState.sourceNodeId);
    // Check if this is actually a decision node (could be mistyped as BOT but have decision data)
    const isActuallyDecisionNode = sourceNode?.data.entityType === "DECISION" || 
                                   (sourceNode?.data.decisionData && Object.keys(sourceNode.data.decisionData).length > 0);
    const isAddingToDecisionNode = isActuallyDecisionNode;
    
    const shouldReorganize = !modalState.isEditMode && !isAddingChildNode && !isAddingToDecisionNode;

    if (modalState.isEditMode && modalState.nodeToEdit) {
      // Update the existing node
      // FIXED: Use the modal state tool calls (which contain the user's updates from NodeModal)
    // The modal state has the NEW truth that the user just entered
    
    // Use modal state tool calls (user's latest input) for BOT nodes with tool calls enabled
    const toolCallsToUse = modalState.newNodeEntityType === "BOT" && modalState.hasToolCall
      ? (modalState.toolCalls || []) // Use the updated tool calls from modal state
      : [];
    
    // Process response variables from tool calls and add them to global registry
    if (toolCallsToUse.length > 0) {
      processResponseVariables(toolCallsToUse);
    }

    // Get the old identifier before updating to check for reference updates
    const oldIdentifier = nodes.find(n => n.id === modalState.nodeToEdit)?.data?.identifier;
    const newIdentifier = modalState.newNodeIdentifier;
    const identifierChanged =
      typeof oldIdentifier === "string" && oldIdentifier.trim() !== "" &&
      typeof newIdentifier === "string" && newIdentifier.trim() !== "" &&
      oldIdentifier !== newIdentifier;
      
      setNodes((nds) =>
        nds.map((node) => {
          if (node.id === modalState.nodeToEdit) {
            const updatedNode = {
              ...node,
              data: {
                ...node.data,
                content: modalState.newNodeContent,
                prefix: modalState.newNodePrefix,
                suffix: modalState.newNodeSuffix,
                decisionData: modalState.newNodeEntityType === "DECISION" ? modalState.decisionData : undefined,
                entityType: modalState.newNodeEntityType,
                identifier: modalState.newNodeIdentifier,
                isDestinationNode: modalState.isDestinationNode, // Update destination node status
                potentialStart: modalState.potentialStart, // Update potential start status
                placeholders: nodePlaceholders,
                toolCalls: toolCallsToUse, // Use modal state tool calls (user's latest updates from NodeModal)
                variableAssignments: modalState.variableAssignments, // Add variable assignments
              },
            };
            return updatedNode;
          } else if (identifierChanged && node.data.entityType === "DECISION" && node.data.decisionData) {
            // Update decision node references to the changed identifier
            const updatedDecisionData = { ...node.data.decisionData };
            let needsUpdate = false;
            
            if (updatedDecisionData.truePath === oldIdentifier) {
              updatedDecisionData.truePath = newIdentifier;
              needsUpdate = true;
            }
            
            if (updatedDecisionData.falsePath === oldIdentifier) {
              updatedDecisionData.falsePath = newIdentifier;
              needsUpdate = true;
            }
            
            if (needsUpdate) {
              return {
                ...node,
                data: {
                  ...node.data,
                  decisionData: updatedDecisionData
                }
              };
            }
          }
          return node;
        })
      );

      // Handle nextNodeId/nextNodeIds edge management for non-DECISION nodes
      if (modalState.newNodeEntityType !== "DECISION" && modalState.nodeToEdit) {
        setTimeout(() => {
          setEdges(currentEdges => {
            // Remove existing outgoing edges from this node (but keep all edges from other nodes)
            const filteredEdges = currentEdges.filter(edge => 
              edge.source !== modalState.nodeToEdit
            );
            // Determine all targets from nextNodeIds (multi) or fallback to nextNodeId
            const targets = (modalState.nextNodeIds && modalState.nextNodeIds.length > 0)
              ? modalState.nextNodeIds
              : (modalState.nextNodeId && modalState.nextNodeId !== "" ? [modalState.nextNodeId] : []);

            const updatedEdges = [...filteredEdges];
            if (targets.length > 0 && !modalState.isDestinationNode) {
              for (const targetId of targets) {
                const newEdgeId = `e${modalState.nodeToEdit}-${targetId}`;
                const edgeExists = updatedEdges.some(edge => edge.id === newEdgeId);
                if (!edgeExists) {
                  const isUp = isUpstreamConnection(modalState.nodeToEdit!, String(targetId), nodes, updatedEdges);
                  const targetNode = nodes.find(n => n.id === String(targetId));
                  updatedEdges.push({
                    id: newEdgeId,
                    source: modalState.nodeToEdit,
                    target: targetId,
                    type: "custom",
                    targetHandle: "target",
                    data: targetNode?.data?.isDestinationNode ? { toDestination: true } : undefined,
                    ...(isUp ? { style: { ...UPSTREAM_EDGE_STYLE } } : {})
                  });
                }
              }
              // REMOVED: Old repositioning logic - now using customReorganizeTree
              // The reorganization will be triggered after all edits are complete
            }

            return updatedEdges;
          });
        }, 10);
      }
      
      // Create decision edges if this is a decision node
      if (modalState.newNodeEntityType === "DECISION" && modalState.decisionData && modalState.nodeToEdit) {
        setTimeout(() => {
          setEdges(currentEdges => {
            const nonDecisionEdges = currentEdges.filter(edge => 
              edge.source !== modalState.nodeToEdit || 
              (!edge.id.includes("_true") && !edge.id.includes("_false"))
            );
            
            const newDecisionEdges: Edge[] = [];
            
            if (modalState.decisionData.truePath) {
              const truePathNodeId = resolveIdentifierToNodeId(modalState.decisionData.truePath, nodes);
              if (truePathNodeId) {
                const isUp = isUpstreamConnection(modalState.nodeToEdit!, truePathNodeId, nodes, currentEdges);
                newDecisionEdges.push({
                  id: `e${modalState.nodeToEdit}-${truePathNodeId}_true`,
                  source: modalState.nodeToEdit,
                  target: truePathNodeId,
                  sourceHandle: "true",
                  targetHandle: "target",
                  type: "custom",
                  label: "True",
                  style: isUp ? { ...UPSTREAM_EDGE_STYLE } : { stroke: "#52c41a", strokeWidth: 2 },
                  labelStyle: { fill: "#52c41a", fontWeight: 600 },
                  labelBgStyle: { fill: "#f6ffed" },
                  data: (nodes.find(n => n.id === truePathNodeId)?.data as any)?.isDestinationNode ? { toDestination: true } : undefined
                });
              }
            }
            
            if (modalState.decisionData.falsePath) {
              const falsePathNodeId = resolveIdentifierToNodeId(modalState.decisionData.falsePath, nodes);
              if (falsePathNodeId) {
                const isUp = isUpstreamConnection(modalState.nodeToEdit!, falsePathNodeId, nodes, currentEdges);
                newDecisionEdges.push({
                  id: `e${modalState.nodeToEdit}-${falsePathNodeId}_false`,
                  source: modalState.nodeToEdit,
                  target: falsePathNodeId,
                  sourceHandle: "false",
                  targetHandle: "target",
                  type: "custom",
                  label: "False",
                  style: isUp ? { ...UPSTREAM_EDGE_STYLE } : { stroke: "#f5222d", strokeWidth: 2 },
                  labelStyle: { fill: "#f5222d", fontWeight: 600 },
                  labelBgStyle: { fill: "#fff2f0" },
                  data: (nodes.find(n => n.id === falsePathNodeId)?.data as any)?.isDestinationNode ? { toDestination: true } : undefined
                });
              }
            }
            
            return deduplicateEdges([...nonDecisionEdges, ...newDecisionEdges]);
          });
        }, 50);
        
        // Skip the decision-specific reorganization - let the general one handle it
        }
        
        // Update node handlers immediately after editing to ensure the UI reflects the new data
        setTimeout(() => {
          updateNodeHandlers();
        }, 0);

    } else if (isFirstNode) {
      // Replace the placeholder node with the first real node
      debugLog(`Replacing placeholder node with first real node. isFirstNode: ${isFirstNode}, sourceNodeId: ${modalState.sourceNodeId}`);
      
      // Ensure the first real node gets ID 1
      resetNodeIdCounter();
      
      // Calculate the sequential ID outside of the setNodes callback to prevent multiple calls
      const sequentialId = getNextNodeId().toString();
      
      // Prepare tool call data if this is a BOT node and has tool call enabled
      let toolCallsData: ToolCall[] = [];
      if (modalState.newNodeEntityType === "BOT" && modalState.hasToolCall) {
        toolCallsData = modalState.toolCalls.filter((tc: any) => tc.name && tc.name.trim());
        
        // Process response variables from tool calls and add them to global registry
        if (toolCallsData.length > 0) {
          processResponseVariables(toolCallsData);
        }
      }
      
      setNodes((nds) =>
        nds.map((node) => {
          if (node.id === modalState.sourceNodeId) {
            return {
              ...node,
              id: sequentialId, // Use sequential ID as actual node ID
              data: {
                ...node.data,
                label: modalState.newNodeEntityType,
                content: modalState.newNodeContent,
                prefix: modalState.newNodePrefix,
                suffix: modalState.newNodeSuffix,
                decisionData: modalState.newNodeEntityType === "DECISION" ? modalState.decisionData : undefined,
                isPlaceholder: false,
                hasChildren: false,
                entityType: modalState.newNodeEntityType,
                identifier: modalState.newNodeIdentifier,
                isDestinationNode: modalState.isDestinationNode, // Set destination node status
                potentialStart: modalState.potentialStart, // Set potential start status
                placeholders: nodePlaceholders,
                globalRegistry: globalVariableRegistry,
                toolCalls: toolCallsData, // Add tool calls data
                variableAssignments: modalState.variableAssignments, // Add variable assignments
                onPlaceholderClick: () => {},
                onAddNode: () => {},
                onEditPlaceholder: () => {},
                onEditNode: () => {},
                onDeleteNode: () => {},
              },
            };
          }
          return node;
        })
      );
      
      // Also update any edges that reference the old placeholder ID
      setEdges((eds) =>
        eds.map((edge) => ({
          ...edge,
          source: edge.source === modalState.sourceNodeId ? sequentialId : edge.source,
          target: edge.target === modalState.sourceNodeId ? sequentialId : edge.target,
        }))
      );
      
      setIsFirstNode(false);
      
      // Create decision edges if this is a decision node (first node scenario)
      if (modalState.newNodeEntityType === "DECISION" && modalState.decisionData) {
        setTimeout(() => {
          setEdges(currentEdges => {
            const nonDecisionEdges = currentEdges.filter(edge => 
              edge.source !== sequentialId || 
              (!edge.id.includes("_true") && !edge.id.includes("_false"))
            );
            
            const newDecisionEdges: Edge[] = [];
            
            if (modalState.decisionData.truePath) {
              const truePathNodeId = resolveIdentifierToNodeId(modalState.decisionData.truePath, nodes);
              if (truePathNodeId) {
                const isUp = isUpstreamConnection(sequentialId, truePathNodeId, nodes, currentEdges);
                newDecisionEdges.push({
                  id: `e${sequentialId}-${truePathNodeId}_true`,
                  source: sequentialId,
                  target: truePathNodeId,
                  type: "custom",
                  label: "True",
                  style: isUp ? { ...UPSTREAM_EDGE_STYLE } : { stroke: "#52c41a", strokeWidth: 2 },
                  labelStyle: { fill: "#52c41a", fontWeight: 600 },
                  labelBgStyle: { fill: "#f6ffed" }
                });
              }
            }
            
            if (modalState.decisionData.falsePath) {
              const falsePathNodeId = resolveIdentifierToNodeId(modalState.decisionData.falsePath, nodes);
              if (falsePathNodeId) {
                const isUp = isUpstreamConnection(sequentialId, falsePathNodeId, nodes, currentEdges);
                newDecisionEdges.push({
                  id: `e${sequentialId}-${falsePathNodeId}_false`,
                  source: sequentialId,
                  target: falsePathNodeId,
                  type: "custom",
                  label: "False",
                  style: isUp ? { ...UPSTREAM_EDGE_STYLE } : { stroke: "#f5222d", strokeWidth: 2 },
                  labelStyle: { fill: "#f5222d", fontWeight: 600 },
                  labelBgStyle: { fill: "#fff2f0" }
                });
              }
            }
            
            if (newDecisionEdges.length > 0) {
              saveToHistory("connect");
            }
            return deduplicateEdges([...nonDecisionEdges, ...newDecisionEdges]);
          });
        }, 100);
      }
      
      // Trigger repositioning after state update
      if (shouldReorganize) {
        setTimeout(() => {
          customReorganizeTree();
        }, 150);
      }
      
      // Always reorganize after editing a node to ensure consistent layout
      setTimeout(() => {
        customReorganizeTree();
      }, 150);
    } else {
      // Adding a child node to an existing node
      debugLog(`Adding child node to existing node. isFirstNode: ${isFirstNode}, sourceNodeId: ${modalState.sourceNodeId}`);
      const parentNode = nodes.find((node) => node.id === modalState.sourceNodeId);
      
      // Calculate initial position for the new node
      // Even though reorganization will run, we need a reasonable starting position
      let newPosition = { x: 250, y: 100 };
      
      if (parentNode) {
        // Place it near the parent temporarily
        // The reorganization will handle the final positioning
            newPosition = {
              x: parentNode.position.x,
          y: parentNode.position.y + 180
        };
      }
      
      // Add identifier to the list if it's a new one
      if (modalState.newNodeIdentifier && 
          !identifiers.includes(modalState.newNodeIdentifier)) {
        setIdentifiers(prev => [...prev, modalState.newNodeIdentifier]);
      }
      
      // Calculate the sequential ID outside of the node creation to prevent multiple calls
      const sequentialId = getNextNodeId().toString();
      
      // Store the decision node ID for later repositioning if this is a DECISION node
      let newDecisionNodeId: string | null = null;
      if (modalState.newNodeEntityType === "DECISION") {
        newDecisionNodeId = sequentialId;
      }
      
      // Prepare tool call data if this is a BOT node and has tool call enabled
      let toolCallsData: ToolCall[] = [];
      if (modalState.newNodeEntityType === "BOT" && modalState.hasToolCall) {
        toolCallsData = modalState.toolCalls.filter((tc: any) => tc.name && tc.name.trim());
        
        // Process response variables from tool calls and add them to global registry
        if (toolCallsData.length > 0) {
          processResponseVariables(toolCallsData);
        }
      }
      
      // Create new node with opposite entity type
      const newNode: Node = {
        id: sequentialId,
        type: "custom",
        position: newPosition,
        data: {
          label: modalState.newNodeEntityType,
          content: modalState.newNodeContent,
          prefix: modalState.newNodePrefix,
          suffix: modalState.newNodeSuffix,
          decisionData: modalState.newNodeEntityType === "DECISION" ? modalState.decisionData : undefined,
          isPlaceholder: false,
          hasChildren: false,
          entityType: modalState.newNodeEntityType,
          identifier: modalState.newNodeIdentifier,
          isDestinationNode: modalState.isDestinationNode, // Set destination node status
          potentialStart: modalState.potentialStart, // Set potential start status
          isDescendantOfDestination: false,
          placeholders: nodePlaceholders,
          globalRegistry: globalVariableRegistry,
          toolCalls: toolCallsData, // Add tool calls data
          variableAssignments: modalState.variableAssignments, // Add variable assignments
          onPlaceholderClick: () => {},
          onAddNode: () => {},
          onEditPlaceholder: () => {},
          onEditNode: () => {},
          onDeleteNode: () => {},
        },
      };

      // For decision node children, mark as manually moved BEFORE adding to prevent any repositioning
      const isAddingToDecisionNode = parentNode?.data.entityType === "DECISION";
      // Don't mark as manually positioned - allow reorganization to handle all nodes
      
            // Add the node and track the updated nodes list
      const updatedNodes = [...nodes, newNode];
      setNodes(updatedNodes);
      
      // Calculate all edges that will be created
      let allNewEdges: Edge[] = [...edges];
      
      // Handle next node assignment for new nodes (creates parent-child relationship)
      if (modalState.nextNodeId && modalState.nextNodeId !== "" && modalState.newNodeEntityType !== "DECISION") {
            const nextEdgeId = `e${newNode.id}-${modalState.nextNodeId}`;
        const existingNextEdge = allNewEdges.find(edge => edge.id === nextEdgeId);
        if (!existingNextEdge) {
          const isUp = isUpstreamConnection(newNode.id, modalState.nextNodeId, nodes, allNewEdges);
            const nextEdge: Edge = {
              id: nextEdgeId,
              source: newNode.id,
              target: modalState.nextNodeId,
              type: "custom",
              targetHandle: "target",
              ...(isUp ? { style: { ...UPSTREAM_EDGE_STYLE } } : {})
            };
          allNewEdges.push(nextEdge);
        }
      }
        
      
      if (isAddingToDecisionNode) {
        // Auto-assign the new node to the appropriate decision path
        const decisionData = sourceNode.data.decisionData || { truePath: null, falsePath: null, conditions: [] };
        const hasTruePath = !!decisionData.truePath;
        const hasFalsePath = !!decisionData.falsePath;
        
        const updatedDecisionData = { ...decisionData };
        
        // Use identifier if available, otherwise use node ID (for backward compatibility)
        const nodeReference = newNode.data.identifier || newNode.id;
        
        if (!hasTruePath && !hasFalsePath) {
          // No paths exist, set as true path
          updatedDecisionData.truePath = nodeReference;
        } else if (hasTruePath && !hasFalsePath) {
          // Only true path exists, set as false path
          updatedDecisionData.falsePath = nodeReference;
        } else if (!hasTruePath && hasFalsePath) {
          // Only false path exists, set as true path
          updatedDecisionData.truePath = nodeReference;
        }
        
        // Update the decision node in our updated nodes array
        updatedNodes.forEach((node, index) => {
          if (node.id === modalState.sourceNodeId) {
            updatedNodes[index] = { ...node, data: { ...node.data, decisionData: updatedDecisionData } };
          }
        });
        
        // Also update via setNodes for React state
        setNodes(nds => nds.map(node => 
          node.id === modalState.sourceNodeId 
            ? { ...node, data: { ...node.data, decisionData: updatedDecisionData } }
            : node
        ));
        
        // Create decision edge
        const isTrue = updatedDecisionData.truePath === nodeReference;
        const edgeLabel = isTrue ? "True" : "False";
        const edgeStyle = isTrue 
          ? { stroke: "#52c41a", strokeWidth: 2 }
          : { stroke: "#f5222d", strokeWidth: 2 };
        const labelStyle = isTrue 
          ? { fill: "#52c41a", fontWeight: 600 }
          : { fill: "#f5222d", fontWeight: 600 };
        const labelBgStyle = isTrue 
          ? { fill: "#f6ffed" }
          : { fill: "#fff2f0" };
        
        const decisionEdgeId = `e${modalState.sourceNodeId}-${newNode.id}_${isTrue ? "true" : "false"}`;
        const existingDecisionEdge = allNewEdges.find(edge => edge.id === decisionEdgeId);
        
        if (!existingDecisionEdge) {
          const decisionEdge: Edge = {
            id: decisionEdgeId,
            source: modalState.sourceNodeId,
            target: newNode.id,
            type: "custom",
            targetHandle: "target",
            label: edgeLabel,
            style: edgeStyle,
            labelStyle: labelStyle,
            labelBgStyle: labelBgStyle
          };
          allNewEdges.push(decisionEdge);
        }
      } else {
        // Regular node addition - create edge from parent to new node
          const edgeId = `e${modalState.sourceNodeId}-${newNode.id}`;
        const existingEdge = allNewEdges.find(edge => edge.id === edgeId);
        
        if (!existingEdge) {
          const isUp = isUpstreamConnection(modalState.sourceNodeId!, newNode.id, nodes, allNewEdges);
            const newEdge: Edge = {
              id: edgeId,
              source: modalState.sourceNodeId,
              target: newNode.id,
              type: "custom",
              targetHandle: "target",
              ...(isUp ? { style: { ...UPSTREAM_EDGE_STYLE } } : {})
            };
          allNewEdges.push(newEdge);
        }
      }
      
      // Create decision edges if this is a decision node AND we're not adding to another decision node
      if (modalState.newNodeEntityType === "DECISION" && modalState.decisionData && !isAddingToDecisionNode) {
        // Remove any existing decision edges from this node
        allNewEdges = allNewEdges.filter(edge => 
              edge.source !== sequentialId || 
              (!edge.id.includes("_true") && !edge.id.includes("_false"))
            );
            
            if (modalState.decisionData.truePath) {
              const truePathNodeId = resolveIdentifierToNodeId(modalState.decisionData.truePath, nodes);
              if (truePathNodeId) {
            allNewEdges.push({
                  id: `e${sequentialId}-${truePathNodeId}_true`,
                  source: sequentialId,
                  target: truePathNodeId,
                  sourceHandle: "true",
                  targetHandle: "target",
                  type: "custom",
                  label: "True",
                  style: { stroke: "#52c41a", strokeWidth: 2 },
                  labelStyle: { fill: "#52c41a", fontWeight: 600 },
                  labelBgStyle: { fill: "#f6ffed" }
                });
              }
            }
            
            if (modalState.decisionData.falsePath) {
              const falsePathNodeId = resolveIdentifierToNodeId(modalState.decisionData.falsePath, nodes);
              if (falsePathNodeId) {
            allNewEdges.push({
                  id: `e${sequentialId}-${falsePathNodeId}_false`,
                  source: sequentialId,
                  target: falsePathNodeId,
                  sourceHandle: "false",
                  targetHandle: "target",
                  type: "custom",
                  label: "False",
                  style: { stroke: "#f5222d", strokeWidth: 2 },
                  labelStyle: { fill: "#f5222d", fontWeight: 600 },
                  labelBgStyle: { fill: "#fff2f0" }
                });
              }
            }
      }
      
      // The nextNodeId edge was already handled above
      
      // Apply all edge updates at once
      setEdges(allNewEdges);
            saveToHistory("connect");
      
      // For decision nodes, mark them as pending reorganization
      if (modalState.newNodeEntityType === "DECISION") {
        
        // Store this decision node as pending reorganization
        // When its height is reported via onNodeResize, we'll trigger reorganization
        setNodes(currentNodes => 
          currentNodes.map(node => 
            node.id === sequentialId 
              ? { ...node, data: { ...node.data, _pendingReorganization: true } }
              : node
          )
        );
      } else {
        // Non-decision nodes can reorganize immediately
        customReorganizeTree(updatedNodes, allNewEdges);
      }
    }

    // Check if this is a decision node with newly created children
    const hasCreatedDecisionChildren = modalState.hasCreatedDecisionChildren === true;

    if (hasCreatedDecisionChildren) {
      // Add delay before closing modal for decision nodes with new children
      
      // Set a flag to indicate we're in delayed close state (to hide validation errors)
      setModalState((prev: ModalState) => ({ ...prev, isReorganizing: true }));
      
      setTimeout(() => {
        resetModalState();
      }, 500);
    } else {
      // Reset form state immediately for other cases
      resetModalState();
    }
  }, [
    modalState,
    hasChildWithIdentifier,
    edges,
    nodes,
    placeholders,
    globalVariableRegistry,
    setGlobalVariableRegistry,
    saveToHistory,
    isFirstNode,
    setNodes,
    setIsFirstNode,

    identifiers,
    setIdentifiers,
    setEdges,
    resetModalState,
    updateNodeHandlers,
    repositionChildrenCentered
  ]);

  const handleCancel = useCallback(() => {
    resetModalState();
    setPlaceholders({});
  }, [resetModalState, setPlaceholders]);

  // Helper function to clean up decision paths when nodes are deleted
  const cleanupDecisionPaths = useCallback((deletedNodeIds: string[], deletedIdentifiers: string[]) => {
    setNodes(currentNodes => 
      currentNodes.map(node => {
        if (node.data.entityType === "DECISION" && node.data.decisionData) {
          const decisionData = { ...node.data.decisionData };
          let pathsChanged = false;
          
          // Check if truePath references a deleted node (by identifier or node ID)
          if (decisionData.truePath) {
            const isDeletedByIdentifier = deletedIdentifiers.includes(decisionData.truePath);
            const isDeletedByNodeId = deletedNodeIds.includes(decisionData.truePath);
            if (isDeletedByIdentifier || isDeletedByNodeId) {
              decisionData.truePath = null;
              pathsChanged = true;
            }
          }
          
          // Check if falsePath references a deleted node (by identifier or node ID)
          if (decisionData.falsePath) {
            const isDeletedByIdentifier = deletedIdentifiers.includes(decisionData.falsePath);
            const isDeletedByNodeId = deletedNodeIds.includes(decisionData.falsePath);
            if (isDeletedByIdentifier || isDeletedByNodeId) {
              decisionData.falsePath = null;
              pathsChanged = true;
            }
          }
          
          // Return updated node if paths changed
          if (pathsChanged) {
            return {
              ...node,
              data: {
                ...node.data,
                decisionData
              }
            };
          }
        }
        return node;
      })
    );
  }, [setNodes]);

  // Delete a single node
  const deleteNode = useCallback((nodeId: string) => {
    // Find the node
    const nodeToDelete = nodes.find(node => node.id === nodeId);
    if (!nodeToDelete) return;
    
    // Save current state to history
    saveToHistory("delete");
    
    // Check if this is the last node
    const isLastNode = nodes.length === 1;
    
    if (isLastNode) {
      // For the last node, set a clean initial state instead of filtering
      setTimeout(() => {
        setNodes(initialNodes);
        setEdges(initialEdges);
        setIsFirstNode(true);
        
        message.success("Conversation Deleted");
      }, 50);
    } else {
      // Clean up decision paths that reference the deleted node
      const deletedIdentifier = nodeToDelete.data.identifier;
      cleanupDecisionPaths([nodeId], deletedIdentifier ? [deletedIdentifier] : []);
      
      // Delete the node from nodes array
      setNodes(nodes => nodes.filter(node => node.id !== nodeId));
      
      // Delete any edges connecting to this node
      setEdges(edges => edges.filter(edge => 
        edge.source !== nodeId && edge.target !== nodeId
      ));
      
      // Trigger reorganization after deletion
      setTimeout(() => {
        customReorganizeTree();
      }, 150);
      
      message.success("Node deleted");
    }
  }, [nodes, saveToHistory, setNodes, setEdges, setIsFirstNode, initialNodes, initialEdges, cleanupDecisionPaths]);

  // Delete a node and all its descendants (branch)
  const deleteBranch = useCallback((nodeId: string) => {
    // Find all descendants
    const descendantIds = getNodeDescendants(nodeId, nodes, edges);
    if (!descendantIds.length) {
      // If no descendants, fallback to single node delete
      deleteNode(nodeId);
      return;
    }
    
    // Save current state to history
    saveToHistory("delete");
    
    // Include the root node of the branch
    const nodesToDelete = [nodeId, ...descendantIds];
    
    // Check if we're deleting all nodes
    const isLastBranch = nodes.length === nodesToDelete.length;
    
    if (isLastBranch) {
      // For the last branch, set a clean initial state instead of filtering
      setTimeout(() => {
        setNodes(initialNodes);
        setEdges(initialEdges);
        setIsFirstNode(true);
        
        message.success("Tree deleted");
      }, 50);
    } else {
      // Collect identifiers from all nodes being deleted
      const deletedIdentifiers = nodesToDelete
        .map(id => nodes.find(n => n.id === id)?.data.identifier)
        .filter(identifier => identifier) as string[];
      
      // Clean up decision paths that reference any of the deleted nodes
      cleanupDecisionPaths(nodesToDelete, deletedIdentifiers);
      
      // Delete all nodes in the branch
      setNodes(nodes => nodes.filter(node => !nodesToDelete.includes(node.id)));
      
      // Delete all edges connected to any of these nodes
      setEdges(edges => edges.filter(edge => 
        !nodesToDelete.includes(edge.source) && !nodesToDelete.includes(edge.target)
      ));
      
      // Trigger reorganization after deletion
      setTimeout(() => {
        customReorganizeTree();
      }, 150);
      
      message.success(`Branch with ${nodesToDelete.length} nodes deleted`);
    }
  }, [nodes, edges, getNodeDescendants, deleteNode, saveToHistory, setNodes, setEdges, setIsFirstNode, initialNodes, initialEdges, cleanupDecisionPaths]);

  // Show delete node confirmation
  const showDeleteNodeConfirmation = useCallback((nodeId: string) => {
    // This function will be implemented in the main component since it uses setDeleteModalState
    // We'll return a function that can be called with the modal state setters
    return nodeId;
  }, []);

  // Show delete branch confirmation
  const showDeleteBranchConfirmation = useCallback((nodeId: string) => {
    // This function will be implemented in the main component since it uses setDeleteModalState
    // We'll return a function that can be called with the modal state setters
    return nodeId;
  }, []);

  // Handle delete node confirmation
  const handleDeleteNodeConfirm = useCallback(() => {
    if (!deleteModalState.nodeToDeleteFromMenu) return;
    
    if (deleteModalState.deleteChildren) {
      // Delete the node and all its children (branch)
      deleteBranch(deleteModalState.nodeToDeleteFromMenu);
    } else {
      // Delete just the single node
      deleteNode(deleteModalState.nodeToDeleteFromMenu);
    }
    
    resetDeleteModalState();
  }, [deleteModalState.nodeToDeleteFromMenu, deleteModalState.deleteChildren, deleteBranch, deleteNode, resetDeleteModalState]);

  // Handle delete branch confirmation
  const handleDeleteBranchConfirm = useCallback(() => {
    if (!deleteModalState.nodeToDeleteFromMenu) return;
    deleteBranch(deleteModalState.nodeToDeleteFromMenu);
    resetDeleteModalState();
  }, [deleteModalState.nodeToDeleteFromMenu, deleteBranch, resetDeleteModalState]);

  // Handle delete cancellation
  const handleDeleteModalCancel = useCallback(() => {
    resetDeleteModalState();
  }, [resetDeleteModalState]);

  // Custom reorganization function that handles shared children and circular loops
  const customReorganizeTree = useCallback((currentNodes?: Node[], currentEdges?: Edge[], latestNodeDimensions?: { [nodeId: string]: number }) => {
    
    // Use provided dimensions or fall back to the ones from closure
    const dimensionsToUse = latestNodeDimensions || nodeDimensions;

    // Use provided nodes/edges or fall back to the ones from state
    const nodesToUse = currentNodes || nodes;
    const edgesToUse = currentEdges || edges;
    
    // Defensive check to ensure we have arrays
    if (!Array.isArray(nodesToUse) || !Array.isArray(edgesToUse)) {
      console.error("[DEBUG] Invalid state: nodes or edges are not arrays", {
        nodesToUse,
        edgesToUse,
        nodesIsArray: Array.isArray(nodesToUse),
        edgesIsArray: Array.isArray(edgesToUse),
        providedNodes: currentNodes,
        providedEdges: currentEdges,
        stateNodes: nodes,
        stateEdges: edges
      });
      return;
    }
    
    
    if (nodesToUse.length === 0) {
      return;
    }

    // Filter out placeholder nodes
    const realNodes = nodesToUse.filter(node => !node.data.isPlaceholder);
    
    if (realNodes.length === 0) {
      return;
    }
    
    // IMPORTANT: Clear all manually moved nodes to ensure full reorganization
    setManuallyMovedNodes(new Set());
    
    // Also force clear any React Flow internal tracking
    setNodes(currentNodes => {
      return currentNodes.map(node => ({
        ...node,
        dragging: false,
        selected: false,
        // Remove any internal positioning flags
        data: {
          ...node.data,
          manuallyPositioned: false,
          lastManualMove: undefined
        }
      }));
    });
    
    // Build adjacency maps for the graph
    const childrenMap = new Map<string, Set<string>>();
    const parentsMap = new Map<string, Set<string>>();
    
    // Initialize maps
    realNodes.forEach(node => {
      childrenMap.set(node.id, new Set());
      parentsMap.set(node.id, new Set());
    });
    
    // Build relationships from edges
    // We'll identify upstream edges after we find the start node
    const upstreamEdges = new Set<string>();
    

    
    // Find the start node using the specified priority
    let startNode: Node | null = null;
    
    // First, check if the root node is a USER node
    const rootNodes = realNodes.filter(node => {
      const parents = parentsMap.get(node.id) || new Set();
      return parents.size === 0;
    });
    
    // If the root node is a USER node, don't use any start node for reorganization
    if (rootNodes.length > 0 && rootNodes[0].data.entityType === "USER") {
      startNode = rootNodes[0]; // Use the USER root for reorganization but don't treat it as conversation start
    } else {
    // Priority 1: Node marked as starting node
    startNode = realNodes.find(node => node.data.isStartNode) || null;
    
      // Priority 2: Node marked as potential start (only BOT nodes)
    if (!startNode) {
        startNode = realNodes.find(node => node.data.potentialStart && node.data.entityType === "BOT") || null;
    }
    
      // Priority 3: Root BOT nodes (no parents)
    if (!startNode) {
        const rootBotNodes = rootNodes.filter(node => node.data.entityType === "BOT");
        if (rootBotNodes.length > 0) {
          startNode = rootBotNodes[0];
        } else if (rootNodes.length > 0) {
          // If no BOT root nodes, use any root node for layout purposes
        startNode = rootNodes[0];
      }
    }
    
    // Priority 4: Node with lowest ID
    if (!startNode) {
      startNode = realNodes.reduce((lowest, node) => {
        const lowestId = parseInt(lowest.id, 10) || Infinity;
        const nodeId = parseInt(node.id, 10) || Infinity;
        return nodeId < lowestId ? node : lowest;
      });
      }
    }
    
    if (!startNode) {
      return;
    }
    
    
    // Now that we have the start node, identify upstream edges
    // Build a preliminary parent-child relationship to determine tree structure
    const prelimChildrenMap = new Map<string, Set<string>>();
    const prelimParentsMap = new Map<string, Set<string>>();
    
    realNodes.forEach(node => {
      prelimChildrenMap.set(node.id, new Set());
      prelimParentsMap.set(node.id, new Set());
    });
    
    edgesToUse.forEach(edge => {
      prelimChildrenMap.get(edge.source)?.add(edge.target);
      prelimParentsMap.get(edge.target)?.add(edge.source);
    });
    
    // Find the tree depth of each node from the root
    const nodeDepths = new Map<string, number>();
    const depthVisited = new Set<string>();
    const depthQueue: {id: string, depth: number}[] = [{id: startNode.id, depth: 0}];
    
    while (depthQueue.length > 0) {
      const {id, depth} = depthQueue.shift()!;
      if (depthVisited.has(id)) continue;
      
      depthVisited.add(id);
      nodeDepths.set(id, depth);
      
      const children = prelimChildrenMap.get(id) || new Set();
      children.forEach(childId => {
        if (!depthVisited.has(childId)) {
          depthQueue.push({id: childId, depth: depth + 1});
        }
      });
    }
    
    
    // Now identify upstream edges: edges where source has higher or equal depth than target
    edgesToUse.forEach(edge => {
      const sourceDepth = nodeDepths.get(edge.source);
      const targetDepth = nodeDepths.get(edge.target);
      
      if (sourceDepth !== undefined && targetDepth !== undefined) {
        // If source is at same or higher depth than target, it's an upstream edge
        if (sourceDepth >= targetDepth) {
          upstreamEdges.add(`${edge.source}-${edge.target}`);
        }
      }
    });
    
    // Now rebuild the childrenMap and parentsMap excluding upstream edges
    childrenMap.clear();
    parentsMap.clear();
    realNodes.forEach(node => {
      childrenMap.set(node.id, new Set());
      parentsMap.set(node.id, new Set());
    });
    
    edgesToUse.forEach(edge => {
      const edgeKey = `${edge.source}-${edge.target}`;
      if (!upstreamEdges.has(edgeKey)) {
        const sourceChildren = childrenMap.get(edge.source);
        if (sourceChildren) {
          sourceChildren.add(edge.target);
        }
        const targetParents = parentsMap.get(edge.target);
        if (targetParents) {
          targetParents.add(edge.source);
        }
      }
    });
    
    // Add decision node paths to relationships (after upstream edges are excluded)
    realNodes.forEach(node => {
      if (node.data.entityType === "DECISION" && node.data.decisionData) {
        const { truePath, falsePath } = node.data.decisionData;
        
        if (truePath) {
          const trueNode = realNodes.find(n => n.data.identifier === truePath || n.id === truePath);
          if (trueNode) {
            childrenMap.get(node.id)?.add(trueNode.id);
            parentsMap.get(trueNode.id)?.add(node.id);
          }
        }
        
        if (falsePath) {
          const falseNode = realNodes.find(n => n.data.identifier === falsePath || n.id === falsePath);
          if (falseNode) {
            childrenMap.get(node.id)?.add(falseNode.id);
            parentsMap.get(falseNode.id)?.add(node.id);
          }
        }
      }
    });
    
    // Calculate node levels using BFS (handles circular loops)
    const nodeLevels = new Map<string, number>();
    const nodePositions = new Map<string, { x: number; y: number }>();
    const levelNodes = new Map<number, string[]>();
    
    // BFS to assign levels
    const queue: { nodeId: string; level: number }[] = [{ nodeId: startNode.id, level: 0 }];
    const visited = new Set<string>();
    
    
    while (queue.length > 0) {
      const { nodeId, level } = queue.shift()!;
      
      if (visited.has(nodeId)) {
        // Already processed - check if we found a shorter path
        const existingLevel = nodeLevels.get(nodeId)!;
        if (level < existingLevel) {
          // Update to shorter path
          nodeLevels.set(nodeId, level);
          // Remove from old level
          const oldLevelNodes = levelNodes.get(existingLevel) || [];
          levelNodes.set(existingLevel, oldLevelNodes.filter(id => id !== nodeId));
          // Add to new level
          const newLevelNodes = levelNodes.get(level) || [];
          newLevelNodes.push(nodeId);
          levelNodes.set(level, newLevelNodes);
        }
        continue;
      }
      
      visited.add(nodeId);
      nodeLevels.set(nodeId, level);
      
      // Track nodes at each level
      if (!levelNodes.has(level)) {
        levelNodes.set(level, []);
      }
      levelNodes.get(level)!.push(nodeId);
      
      // Add children to queue
      const children = childrenMap.get(nodeId) || new Set();
      children.forEach(childId => {
        if (!visited.has(childId) || nodeLevels.get(childId)! > level + 1) {
          queue.push({ nodeId: childId, level: level + 1 });
        }
      });
    }
    
    // Check for unvisited nodes (standalone nodes not connected to the main tree)
    const unvisitedNodes = realNodes.filter(node => !visited.has(node.id));
    if (unvisitedNodes.length > 0) {
      
      // For each unvisited node, check if it has children that ARE visited
      // If so, position it one level above its highest child
      unvisitedNodes.forEach(node => {
        if (!visited.has(node.id)) {
          const children = childrenMap.get(node.id) || new Set();
          let minChildLevel = Infinity;
          let maxChildLevel = -Infinity;
          let hasVisitedChildren = false;
          
          children.forEach(childId => {
            if (visited.has(childId) && nodeLevels.has(childId)) {
              hasVisitedChildren = true;
              const childLevel = nodeLevels.get(childId)!;
              minChildLevel = Math.min(minChildLevel, childLevel);
              maxChildLevel = Math.max(maxChildLevel, childLevel);
            }
          });
          
          if (hasVisitedChildren) {
            // Simple case: all children are below (same level or consecutive levels)
            // Place parent one level above the highest child
            const nodeLevel = minChildLevel - 1;
            
            // Make sure we don't go negative
            if (nodeLevel >= 0) {
              visited.add(node.id);
              nodeLevels.set(node.id, nodeLevel);
              
              if (!levelNodes.has(nodeLevel)) {
                levelNodes.set(nodeLevel, []);
              }
              levelNodes.get(nodeLevel)!.push(node.id);
              
              // Now we need to check if this placement causes any conflicts
              // and potentially push other nodes down
              const parents = parentsMap.get(node.id) || new Set();
              parents.forEach(parentId => {
                if (nodeLevels.has(parentId) && nodeLevels.get(parentId)! >= nodeLevel) {
                  // Parent is at same or lower level, this is wrong
                }
              });
            }
          }
        }
      });
      
      // Now process any remaining truly standalone nodes
      const remainingUnvisited = realNodes.filter(node => !visited.has(node.id));
      if (remainingUnvisited.length > 0) {
        
        // For each disconnected subgraph, find the roots (nodes with no parents in the subgraph)
        const subgraphRoots: string[] = [];
        
        remainingUnvisited.forEach(node => {
          if (!visited.has(node.id)) {
            const parents = parentsMap.get(node.id) || new Set();
            let hasUnvisitedParent = false;
            
            parents.forEach(parentId => {
              if (!visited.has(parentId)) {
                hasUnvisitedParent = true;
              }
            });
            
            // If no unvisited parents, this is a root of a disconnected subgraph
            if (!hasUnvisitedParent) {
              subgraphRoots.push(node.id);
            }
          }
        });
        
        
        // Process each subgraph starting from its roots
        subgraphRoots.forEach(rootId => {
          if (!visited.has(rootId)) {
            // Start this subgraph at level 1
            const subgraphQueue: { nodeId: string; level: number }[] = [{ nodeId: rootId, level: 1 }];
            
            while (subgraphQueue.length > 0) {
              const { nodeId, level } = subgraphQueue.shift()!;
              
              if (!visited.has(nodeId)) {
                visited.add(nodeId);
                nodeLevels.set(nodeId, level);
                
                if (!levelNodes.has(level)) {
                  levelNodes.set(level, []);
                }
                levelNodes.get(level)!.push(nodeId);
                
                // Add children to queue
                const children = childrenMap.get(nodeId) || new Set();
                children.forEach(childId => {
                  if (!visited.has(childId)) {
                    subgraphQueue.push({ nodeId: childId, level: level + 1 });
                  }
                });
              }
            }
          }
        });
        
        // Handle any remaining nodes that might have circular dependencies
        const stillUnvisited = realNodes.filter(node => !visited.has(node.id));
        if (stillUnvisited.length > 0) {
          stillUnvisited.forEach(node => {
            if (!visited.has(node.id)) {
              visited.add(node.id);
              nodeLevels.set(node.id, 1); // Default to level 1
              if (!levelNodes.has(1)) {
                levelNodes.set(1, []);
              }
              levelNodes.get(1)!.push(node.id);
            }
          });
        }
      }
    }
    
    
    // PHASE 1.5: Adjust levels to ensure proper parent-child relationships
    let adjustmentsMade = true;
    let iterations = 0;
    while (adjustmentsMade && iterations < 10) { // Prevent infinite loops
      adjustmentsMade = false;
      iterations++;
      
      // First: Fix same-level parent-child relationships
      // ONLY enforce this for non-decision nodes and non-upstream edges
      edgesToUse.forEach(edge => {
        const sourceLevel = nodeLevels.get(edge.source);
        const targetLevel = nodeLevels.get(edge.target);
        
        if (sourceLevel !== undefined && targetLevel !== undefined) {
          // Skip self-loops
          if (edge.source === edge.target) {
            return;
          }
          
          // Skip upstream edges (they don't represent parent-child relationships)
          const edgeKey = `${edge.source}-${edge.target}`;
          const isUpstream = upstreamEdges.has(edgeKey);
          if (isUpstream) {
            return;
          }
          
          // Check if source is a decision node
          const sourceNode = realNodes.find(n => n.id === edge.source);
          const isSourceDecision = sourceNode && sourceNode.data.entityType === "DECISION";
          
          // For decision nodes, allow their "children" (true/false paths) to be on the same level
          // This prevents the visual confusion where siblings appear as parent-child
          if (isSourceDecision) {
            return;
          }
          
          // If parent and child are on the same level or child is above parent, move the child down
          if (sourceLevel >= targetLevel) {
            
            // Move the child node to one level below parent
            const newLevel = sourceLevel + 1;
            
            // Update nodeLevels
            nodeLevels.set(edge.target, newLevel);
            
            // Update levelNodes
            const currentLevelNodes = levelNodes.get(targetLevel) || [];
            levelNodes.set(targetLevel, currentLevelNodes.filter(id => id !== edge.target));
            
            if (!levelNodes.has(newLevel)) {
              levelNodes.set(newLevel, []);
            }
            levelNodes.get(newLevel)!.push(edge.target);
            
            adjustmentsMade = true;
          }
        }
      });
      
      // Second: Try to optimize placement - nodes should be just below their lowest parent
      realNodes.forEach(node => {
        const nodeId = node.id;
        const currentLevel = nodeLevels.get(nodeId);
        if (currentLevel === undefined) return;
        
        const parents = parentsMap.get(nodeId) || new Set();
        const children = childrenMap.get(nodeId) || new Set();
        
        // Case 1: Node has parents - should be just below lowest parent
        if (parents.size > 0) {
          // Find the maximum (lowest) parent level
          let maxParentLevel = -1;
          parents.forEach(parentId => {
            const parentLevel = nodeLevels.get(parentId);
            if (parentLevel !== undefined) {
              maxParentLevel = Math.max(maxParentLevel, parentLevel);
            }
          });
          
          // Ideal level is just below the lowest parent
          const idealLevel = maxParentLevel + 1;
          
          // Only move if it would bring the node up (closer to parents)
          if (idealLevel < currentLevel && idealLevel >= 0) {
            // Check if this would violate any child constraints
            let canMove = true;
            
            children.forEach(childId => {
              if (childId !== nodeId) { // Skip self-loops
                const childLevel = nodeLevels.get(childId);
                if (childLevel !== undefined && childLevel <= idealLevel) {
                  canMove = false; // Would put us at same level or below a child
                }
              }
            });
            
            if (canMove) {
              
              // Update nodeLevels
              nodeLevels.set(nodeId, idealLevel);
              
              // Update levelNodes
              const oldLevelNodes = levelNodes.get(currentLevel) || [];
              levelNodes.set(currentLevel, oldLevelNodes.filter(id => id !== nodeId));
              
              if (!levelNodes.has(idealLevel)) {
                levelNodes.set(idealLevel, []);
              }
              levelNodes.get(idealLevel)!.push(nodeId);
              
              adjustmentsMade = true;
            }
          }
        }
        // Case 2: Node has no parents but has children - should be just above highest child
        else if (children.size > 0) {
          // Find the minimum (highest) child level
          let minChildLevel = Infinity;
          children.forEach(childId => {
            if (childId !== nodeId) { // Skip self-loops
              const childLevel = nodeLevels.get(childId);
              if (childLevel !== undefined) {
                minChildLevel = Math.min(minChildLevel, childLevel);
              }
            }
          });
          
          // Ideal level is just above the highest child
          const idealLevel = minChildLevel - 1;
          
          // Move if needed and valid
          if (idealLevel !== currentLevel && idealLevel >= 0 && minChildLevel !== Infinity) {
            
            // Update nodeLevels
            nodeLevels.set(nodeId, idealLevel);
            
            // Update levelNodes
            const oldLevelNodes = levelNodes.get(currentLevel) || [];
            levelNodes.set(currentLevel, oldLevelNodes.filter(id => id !== nodeId));
            
            if (!levelNodes.has(idealLevel)) {
              levelNodes.set(idealLevel, []);
            }
            levelNodes.get(idealLevel)!.push(nodeId);
            
            adjustmentsMade = true;
          }
        }
      });
    }
    
    
    // PHASE 2: Position nodes level by level
    const MIN_VERTICAL_SPACING = 180;
    const HORIZONTAL_SPACING = 280; // Spacing between nodes - equal to node width to ensure each node occupies only its own slot
    const START_X = 400;
    const START_Y = 100;
    const DEFAULT_NODE_HEIGHT = 120;
    const DEFAULT_NODE_WIDTH = 280;
    
    // Position start node
    nodePositions.set(startNode.id, { x: START_X, y: START_Y });
    
    // Track the maximum Y position of nodes at each level (considering their heights)
    const levelMaxY = new Map<number, number>();
    const startNodeHeight = dimensionsToUse[startNode.id] || DEFAULT_NODE_HEIGHT;
    levelMaxY.set(0, START_Y + startNodeHeight);
    
    // Position each level
    levelNodes.forEach((nodesAtLevel, level) => {
      if (level === 0) {
        // Handle level 0 nodes that aren't the start node (e.g., standalone nodes that became parents of the root)
        nodesAtLevel.forEach(nodeId => {
          if (nodeId !== startNode.id && !nodePositions.has(nodeId)) {
            nodePositions.set(nodeId, { x: START_X - 400, y: START_Y }); // Place it to the left of start node
          }
        });
        return;
      }
      
      // Calculate Y position based on the tallest node in the previous level
      const prevLevelMaxY = levelMaxY.get(level - 1) || START_Y;
      const y = prevLevelMaxY + MIN_VERTICAL_SPACING;
      
      // Track the maximum height at this level
      let maxHeightAtLevel = 0;

      // Calculate ideal positions based on parent AND children positions
      const nodeIdealPositions = new Map<string, number>();
      
      nodesAtLevel.forEach(nodeId => {
        const parents = Array.from(parentsMap.get(nodeId) || []);
        const children = Array.from(childrenMap.get(nodeId) || []);
        
        
        let sumX = 0;
        let count = 0;
        let minParentX = Infinity;
        let maxParentX = -Infinity;
        
        // First priority: position based on parents (for top-down flow)
        if (parents.length > 0) {
          // Check if all parents are positioned (they should be from previous levels)
          let allParentsPositioned = true;
        parents.forEach(parentId => {
          const parentPos = nodePositions.get(parentId);
          if (parentPos) {
            sumX += parentPos.x;
            count++;
              minParentX = Math.min(minParentX, parentPos.x);
              maxParentX = Math.max(maxParentX, parentPos.x);
            } else {
              allParentsPositioned = false;
          }
        });
        
          // If we have multiple parents, position between them
          if (count > 1 && allParentsPositioned) {
            // Use the midpoint between leftmost and rightmost parent
            const idealX = (minParentX + maxParentX) / 2;
            nodeIdealPositions.set(nodeId, idealX);
          } else if (count === 1) {
            // Single parent - check if we're the only child
            const parentId = parents[0];
            const siblings = Array.from(childrenMap.get(parentId) || []);
            if (siblings.length === 1) {
              // We're a single child - use parent's exact position
              nodeIdealPositions.set(nodeId, sumX);
            } else {
              // Multiple siblings - use average position
              nodeIdealPositions.set(nodeId, sumX / count);
            }
          } else if (count > 0) {
            // Not all parents positioned - use average
            nodeIdealPositions.set(nodeId, sumX / count);
          }
        }
        
        // Second priority: if no positioned parents, check children
        if (count === 0 && children.length > 0) {
          let minChildX = Infinity;
          let maxChildX = -Infinity;
          children.forEach(childId => {
            const childPos = nodePositions.get(childId);
            if (childPos) {
              sumX += childPos.x;
              count++;
              minChildX = Math.min(minChildX, childPos.x);
              maxChildX = Math.max(maxChildX, childPos.x);
            }
          });
          
          if (count > 1) {
            // Multiple children - position at their center
            const idealX = (minChildX + maxChildX) / 2;
            nodeIdealPositions.set(nodeId, idealX);
          } else if (count > 0) {
            // Single child
            nodeIdealPositions.set(nodeId, sumX / count);
          }
        }
        
        // Default position if no parents or children are positioned
        if (!nodeIdealPositions.has(nodeId)) {
          nodeIdealPositions.set(nodeId, START_X);
        }
        
        // Track the maximum height at this level
        const nodeHeight = dimensionsToUse[nodeId] || DEFAULT_NODE_HEIGHT;
        maxHeightAtLevel = Math.max(maxHeightAtLevel, nodeHeight);
      });
      
      // Update level max Y
      levelMaxY.set(level, y + maxHeightAtLevel);
      
      // SLOT-BASED POSITIONING
      const SLOT_WIDTH = DEFAULT_NODE_WIDTH + HORIZONTAL_SPACING;
      
      // First identify single children
      const singleChildren = new Set<string>();
      nodesAtLevel.forEach(nodeId => {
        const nodeParents = Array.from(parentsMap.get(nodeId) || []);
        if (nodeParents.length === 1) {
          const parentId = nodeParents[0];
          const siblings = Array.from(childrenMap.get(parentId) || []);
          if (siblings.length === 1) {
            singleChildren.add(nodeId);
          }
        }
      });
      
      // Calculate ideal slot for each node based on parent position
      const nodeIdealSlots = new Map<string, number>();
      
      nodesAtLevel.forEach(nodeId => {
        const idealX = nodeIdealPositions.get(nodeId) || START_X;
        const idealSlot = Math.round(idealX / SLOT_WIDTH);
        nodeIdealSlots.set(nodeId, idealSlot);
      });
      
      // Sort nodes by ideal slot, but prioritize single children
      const sortedNodes = [...nodesAtLevel].sort((a, b) => {
        const aIsSingle = singleChildren.has(a);
        const bIsSingle = singleChildren.has(b);
        
        // Single children get priority
        if (aIsSingle && !bIsSingle) return -1;
        if (!aIsSingle && bIsSingle) return 1;
        
        // Otherwise sort by ideal slot
        const aSlot = nodeIdealSlots.get(a) || 0;
        const bSlot = nodeIdealSlots.get(b) || 0;
        return aSlot - bSlot;
      });
      
      
      // Track occupied slots
      const occupiedSlots = new Set<number>();
      const slotAssignments = new Map<number, string>(); // slot -> nodeId for debugging
      const nodeSlots = new Map<string, number>(); // nodeId -> assigned slot
      
      // Position each node
      sortedNodes.forEach(nodeId => {
        const node = realNodes.find(n => n.id === nodeId);
        if (!node) return;

        // All nodes use the same slot-based positioning
        const idealSlot = nodeIdealSlots.get(nodeId) || 0;
        let assignedSlot = idealSlot;

        // Function to check if a slot is available
        const canPlaceAtSlot = (slot: number) => {
          return !occupiedSlots.has(slot);
        };
        
        // If ideal slot won't work, find nearest available
        if (!canPlaceAtSlot(idealSlot)) {
          let distance = 1;
          while (true) {
            // Check slots alternating left and right
            const leftSlot = idealSlot - distance;
            const rightSlot = idealSlot + distance;
            
            if (canPlaceAtSlot(rightSlot)) {
              assignedSlot = rightSlot;
              break;
            }
            if (canPlaceAtSlot(leftSlot)) {
              assignedSlot = leftSlot;
              break;
            }
            
            distance++;
          }
        }
        
        // Calculate actual position from slot
        const x = assignedSlot * SLOT_WIDTH;
        nodePositions.set(nodeId, { x, y });
        
        // Mark only the assigned slot as occupied (since we have enough spacing)
        if (occupiedSlots.has(assignedSlot)) {
          console.warn(`[DEBUG] SLOT CONFLICT! Slot ${assignedSlot} already occupied by ${slotAssignments.get(assignedSlot)}, trying to place ${nodeId}`);
        }
        occupiedSlots.add(assignedSlot);
        slotAssignments.set(assignedSlot, nodeId);

        nodeSlots.set(nodeId, assignedSlot);
      });
      
      // REBALANCING PHASE: Better distribute children around their parents
      if (nodesAtLevel.length > 0) {
        // Group nodes by their primary parent
        const nodesByParent = new Map<string, string[]>();
        
        nodesAtLevel.forEach(nodeId => {
        const parents = Array.from(parentsMap.get(nodeId) || []);
          if (parents.length > 0) {
            // Use the parent with the closest ideal position as the primary parent
            let primaryParent = parents[0];
            let minDistance = Infinity;
            
            parents.forEach(parentId => {
              const parentPos = nodePositions.get(parentId);
              const nodeIdealPos = nodeIdealPositions.get(nodeId);
              if (parentPos && nodeIdealPos !== undefined) {
                const distance = Math.abs(parentPos.x - nodeIdealPos);
                if (distance < minDistance) {
                  minDistance = distance;
                  primaryParent = parentId;
                }
              }
            });
            
            if (!nodesByParent.has(primaryParent)) {
              nodesByParent.set(primaryParent, []);
            }
            nodesByParent.get(primaryParent)!.push(nodeId);
          }
        });
        
        
        // For each parent group, redistribute children evenly
        nodesByParent.forEach((children, parentId) => {
          if (children.length > 1) {
            const parentPos = nodePositions.get(parentId);
            if (!parentPos) return;
            
            const parentSlot = Math.round(parentPos.x / SLOT_WIDTH);
            
            // Sort children by their current positions
            children.sort((a, b) => {
              const slotA = nodeSlots.get(a) || 0;
              const slotB = nodeSlots.get(b) || 0;
              return slotA - slotB;
            });
            
                                     // Calculate ideal distribution - center children under parent
            const childCount = children.length;
            
            // Calculate starting position to center the group under parent
            const idealStartSlot = parentSlot - Math.floor((childCount - 1) / 2);
            
            // Find available slots for all children, allowing gaps if needed
            const targetSlots: number[] = [];
            
            // Try to place children centered under parent, but skip occupied slots
            let slotOffset = 0;
            for (let i = 0; i < childCount; i++) {
              const childId = children[i];
              let foundSlot = false;
              
              // Try slots radiating out from the ideal position
              while (!foundSlot && slotOffset < 20) {
                // Calculate candidate slot based on ideal position and offset
                const candidateSlot = idealStartSlot + i + slotOffset;
                
                // Check if this slot is available
                let slotAvailable = true;
                if (occupiedSlots.has(candidateSlot)) {
                  const occupyingNode = slotAssignments.get(candidateSlot);
                  if (occupyingNode && !children.includes(occupyingNode)) {
                    slotAvailable = false;
                  }
                }
                
                if (slotAvailable) {
                  targetSlots.push(candidateSlot);
                  foundSlot = true;
                } else {
                  // If slot is occupied, we need to shift all remaining children
                  slotOffset += 1;
                }
              }
              
              if (!foundSlot) {
                console.warn(`[DEBUG] Could not find slot for child ${childId} of parent ${parentId}`);
              }
            }
            
            // Position children at the calculated slots
            children.forEach((childId, index) => {
              if (index < targetSlots.length) {
                const targetSlot = targetSlots[index];
                const currentSlot = nodeSlots.get(childId);
                
                if (currentSlot !== undefined && currentSlot !== targetSlot) {
                  // Clear old slot assignment
                  occupiedSlots.delete(currentSlot);
                  slotAssignments.delete(currentSlot);
                  
                  // Assign new slot
                  const newX = targetSlot * SLOT_WIDTH;
                  const currentY = nodePositions.get(childId)?.y || y;
                  nodePositions.set(childId, { x: newX, y: currentY });
                  nodeSlots.set(childId, targetSlot);
                  occupiedSlots.add(targetSlot);
                  slotAssignments.set(targetSlot, childId);
                  
                }
              }
            });
          }
        });
      }
    });
    
    // PHASE 3: Center root node above its children
    
    // Reposition the root node to be centered above its children
    const rootChildren = childrenMap.get(startNode.id) || new Set();
    if (rootChildren.size > 0) {
      let minChildX = Infinity;
      let maxChildX = -Infinity;
      let childCount = 0;
      
      rootChildren.forEach(childId => {
        const childPos = nodePositions.get(childId);
        if (childPos) {
          minChildX = Math.min(minChildX, childPos.x);
          maxChildX = Math.max(maxChildX, childPos.x);
          childCount++;
        }
      });
      
      if (childCount > 0) {
        // Center the root between its leftmost and rightmost children
        const centerX = (minChildX + maxChildX) / 2;
        const currentRootPos = nodePositions.get(startNode.id);
        if (currentRootPos) {
          nodePositions.set(startNode.id, { x: centerX, y: currentRootPos.y });
        }
      }
    }
    
    // Apply the calculated positions
    setNodes(currentNodes => {
      return currentNodes.map(node => {
        const newPos = nodePositions.get(node.id);
        if (newPos) {
          return {
            ...node,
            position: newPos
          };
        }
        return node;
      });
    });
    
    // IMPORTANT: We need to ensure React Flow sees this as a change
    // Use setTimeout to break out of any current update cycle
    setTimeout(() => {
      setNodes(currentNodes => {
        // Create completely new node objects to force React Flow to update
        const updatedNodes = currentNodes.map(node => {
          const newPos = nodePositions.get(node.id);
          if (newPos && (node.position.x !== newPos.x || node.position.y !== newPos.y)) {
            // Create a completely new node object
            return {
              ...node,
              position: { x: newPos.x, y: newPos.y },
              // Force a re-render by updating a timestamp
              data: { ...node.data, lastReorganized: Date.now() }
            };
          }
          return node;
        });
        return updatedNodes;
      });
    }, 100);
    
    
    // Clean up any temporary flags
    const hasTemporaryFlags = realNodes.some((n: Node<NodeData>) => 
      n.data._pendingReorganization === true
    );
    
    if (hasTemporaryFlags) {
      setNodes((currentNodes: Node<NodeData>[]) => 
        currentNodes.map(node => ({
          ...node,
          data: { 
            ...node.data, 
            _pendingReorganization: undefined 
          } as NodeData
        }))
      );
    }
    
  }, [nodes, edges, setNodes, setManuallyMovedNodes, nodeDimensions]);

  return {
    handleOk,
    handleCancel,
    deleteNode,
    deleteBranch,
    showDeleteNodeConfirmation,
    showDeleteBranchConfirmation,
    handleDeleteNodeConfirm,
    handleDeleteBranchConfirm,
    handleDeleteModalCancel,
    calculateSmartPosition,
    repositionChildrenCentered,
    customReorganizeTree
  };
}; 